'use client';

import React, { useState } from 'react';
import ToggleSwitch from '@/Components/ToggleSwitch';
import CookieDecorativeCard, { CookieIconTile, CookieCloseButton } from './CookieDecorativeCard';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  advertising: boolean;
}

interface PreferencesModalProps {
  onClose?: () => void;
  onSave?: (preferences: CookiePreferences) => void;
}

interface CategoryRowProps {
  title: string;
  description: string;
  expanded: boolean;
  onToggleExpand: () => void;
  control: React.ReactNode;
}

function CategoryRow({ title, description, expanded, onToggleExpand, control }: CategoryRowProps) {
  return (
    <div className="rounded-[13px] border border-[#F1F2F9] bg-[#FBFBFE] p-4">
      <div className="flex items-start gap-3">
        {/* Chevron box */}
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#E6F3FD] text-[#292D32]"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          >
            <path
              d="M5 3l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-[#170F49]">{title}</h3>
          <p className="mt-1 text-xs leading-5 text-[#32343B]">{description}</p>
        </div>

        {/* Control (badge or toggle) */}
        <div className="ml-2 mt-0.5 flex-shrink-0">{control}</div>
      </div>
    </div>
  );
}

export default function PreferencesModal({ onClose = () => {}, onSave = () => {} }: PreferencesModalProps) {
  const [analytics, setAnalytics] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [advertising, setAdvertising] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggleExpand = (key: string) => setExpanded((prev) => (prev === key ? null : key));

  const handleAllowAll = () => {
    onSave({ necessary: true, analytics: true, functional: true, advertising: true });
    onClose();
  };

  const handleAllowSelected = () => {
    onSave({ necessary: true, analytics, functional, advertising });
    onClose();
  };

  const handleEscapeKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <>
      {/* Dim overlay (click to close) */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={handleEscapeKey}
        role="button"
        tabIndex={-1}
        aria-label="Close cookie preferences"
      />

      {/* Card — same position as the default banner (bottom-center) */}
      <div
        className="fixed bottom-5 left-1/2 z-[9999] max-h-[92vh] w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie preferences"
      >
        <CookieDecorativeCard className="max-h-[92vh] overflow-y-auto">
          <div className="p-6 sm:p-7">
            {/* Top row: icon + close */}
            <div className="flex items-start justify-between">
              <CookieIconTile />
              <CookieCloseButton onClick={onClose} />
            </div>

            {/* Title */}
            <h2 className="mt-4 text-[26px] font-extrabold leading-tight text-[#040405]">
              Cookies Settings
            </h2>

            {/* Category cards */}
            <div className="mt-5 space-y-3">
              <CategoryRow
                title="Strictly necessary cookies"
                description="These cookies are essential for the website to function properly. They keep our website safe, enable core features, and remember your privacy choices."
                expanded={expanded === 'necessary'}
                onToggleExpand={() => toggleExpand('necessary')}
                control={
                  <span className="inline-flex items-center rounded-[3px] bg-[#DBEAB0] px-3 py-1 text-xs font-semibold text-[#628500]">
                    Always active
                  </span>
                }
              />

              <CategoryRow
                title="Analytics cookies"
                description="These help us understand how visitors interact with the website (Google Tag Manager, Microsoft Clarity) so we can measure and improve performance."
                expanded={expanded === 'analytics'}
                onToggleExpand={() => toggleExpand('analytics')}
                control={<ToggleSwitch checked={analytics} onChange={setAnalytics} />}
              />

              <CategoryRow
                title="Functional cookies"
                description="These enable enhanced functionality and personalization, such as remembering your preferences and settings across visits."
                expanded={expanded === 'functional'}
                onToggleExpand={() => toggleExpand('functional')}
                control={<ToggleSwitch checked={functional} onChange={setFunctional} />}
              />

              <CategoryRow
                title="Advertising cookies"
                description="These may be set through our site by advertising partners to build a profile of your interests and show relevant ads on other sites."
                expanded={expanded === 'advertising'}
                onToggleExpand={() => toggleExpand('advertising')}
                control={<ToggleSwitch checked={advertising} onChange={setAdvertising} />}
              />
            </div>

            {/* Actions */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleAllowAll}
                className="flex-1 border border-[#32343B] px-4 py-2.5 text-sm font-semibold text-[#040405] transition-colors hover:bg-[#32343B] hover:text-white sm:flex-none sm:px-8"
              >
                Allow All
              </button>
              <button
                type="button"
                onClick={handleAllowSelected}
                className="flex-1 border border-[#32343B] px-4 py-2.5 text-sm font-semibold text-[#040405] transition-colors hover:bg-[#32343B] hover:text-white sm:flex-none sm:px-8"
              >
                Allow Selected
              </button>
            </div>
          </div>
        </CookieDecorativeCard>
      </div>
    </>
  );
}
